import { SELFIE_CONFIG as C } from './config'
import type { FaceBox } from './checks'

export interface CropRect {
  sx: number
  sy: number
  sw: number
  sh: number
}

export type CropResult = { ok: true; rect: CropRect } | { ok: false; reason: 'lowRes' }

/**
 * Portrait crop around the face (pixel units of the source frame): the face
 * box is ~`faceShare` of the crop width, centred, clamped to the frame. No
 * upscaling: if the crop's short side would be under `minShortSide` it is
 * grown towards it when the frame allows, otherwise the camera is too weak.
 * With `allowSmall` the native size is used instead of failing. Without a face box (fallback mode) it centre-crops the whole frame.
 */
export function computeCrop(
  frameW: number,
  frameH: number,
  face: FaceBox | null,
  allowSmall = false,
): CropResult {
  const min = C.capture.minShortSide
  let cw: number
  let ch: number
  let cx: number
  let cy: number
  if (face) {
    cw = (face.w * frameW) / C.capture.faceShare
    ch = cw / C.capture.aspect
    cx = (face.x + face.w / 2) * frameW
    cy = (face.y + face.h / 2) * frameH
  } else {
    ch = Math.min(frameH, frameW / C.capture.aspect)
    cw = Math.min(frameW, ch * C.capture.aspect)
    cx = frameW / 2
    cy = frameH / 2
  }
  cw = Math.min(cw, frameW)
  ch = Math.min(ch, frameH)
  cw = Math.max(cw, Math.min(min, frameW))
  ch = Math.max(ch, Math.min(min, frameH))
  if (!allowSmall && Math.min(cw, ch) < min) return { ok: false, reason: 'lowRes' }
  const sx = Math.min(Math.max(cx - cw / 2, 0), frameW - cw)
  const sy = Math.min(Math.max(cy - ch / 2, 0), frameH - ch)
  return {
    ok: true,
    rect: { sx: Math.round(sx), sy: Math.round(sy), sw: Math.floor(cw), sh: Math.floor(ch) },
  }
}

export type EncodeResult =
  | { ok: true; dataUrl: string; width: number; height: number }
  | { ok: false; reason: 'lowRes' | 'tooLarge' | 'failed' }

/** Approximate decoded size of a base64 data URL. */
export function dataUrlBytes(dataUrl: string): number {
  const b64 = dataUrl.split(',')[1] ?? ''
  return Math.floor((b64.length * 3) / 4)
}

/**
 * Crops `source` (an unmirrored video frame or canvas) to JPEG, lowering the
 * quality step by step if it exceeds the server limit.
 */
export function encodePhoto(
  source: CanvasImageSource,
  frameW: number,
  frameH: number,
  face: FaceBox | null,
  allowSmall = false,
): EncodeResult {
  const crop = computeCrop(frameW, frameH, face, allowSmall)
  if (!crop.ok) return { ok: false, reason: 'lowRes' }
  const { sx, sy, sw, sh } = crop.rect
  const canvas = document.createElement('canvas')
  canvas.width = sw
  canvas.height = sh
  const ctx = canvas.getContext('2d')
  if (!ctx) return { ok: false, reason: 'failed' }
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh)
  for (const q of [C.capture.jpegQuality, 0.8, 0.7, 0.6]) {
    const dataUrl = canvas.toDataURL('image/jpeg', q)
    if (!dataUrl.startsWith('data:image/jpeg')) return { ok: false, reason: 'failed' }
    if (dataUrlBytes(dataUrl) <= C.capture.maxBytes) {
      return { ok: true, dataUrl, width: sw, height: sh }
    }
  }
  return { ok: false, reason: 'tooLarge' }
}
